import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { env } from '../../config'
import styles from './waiting.module.scss';
import { UserContext, LanguageContext } from '../../contexts';
import Loader from '../../components/Loader';
import Alert from '../../components/Alert';
import { alertType } from '../../components/Alert/alertType';
import { userApi } from '../../api/user';
import { useLabels } from '../../hooks';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Title from '../../components/Title';

const pageKey = "waiting";

const Waiting = () => {
    const { getJwtToken, listProducts, pingUserData } = useContext(UserContext);
    const [showLoader, setShowLoader] = useState(true);
    const [attempts, setAttempts] = useState(0);
    const MAX_ATTEMPTS = 6; // 6 attempts * 10 seconds = 60 seconds total
    const [completed, setCompleted] = useState(false);
    const { getLabel, getLocalUrlByLanguage } = useLabels();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const status = searchParams.get('status');
    const alertRef = useRef();
    const hasRefreshedUserData = useRef(false);
    const syncUserData = useCallback(async () => {
        if (hasRefreshedUserData.current || typeof pingUserData !== 'function') {
            return;
        }
        hasRefreshedUserData.current = true;
        try {
            await pingUserData();
        } catch (error) {
            console.error('Failed to refresh user data after billing:', error);
        }
    }, [pingUserData]);
    
    const contentToAlert = useMemo(() => {
        return (
            <p style={{ fontWeight: 400, fontSize: '1.2rem', marginBottom: 0 }}>
                {getLabel('billing_failed_content_1')}<br />{getLabel('billing_failed_content_2')}</p>
        )
    }, [getLabel, getLocalUrlByLanguage]);

    const contentToAlertCreditCard = useMemo(() => {
        return (
            <p style={{ fontWeight: 400, fontSize: '1.2rem', marginBottom: 0 }}>
                {getLabel('billing_invalid_credit_card_details_content_1')}<br />{getLabel('billing_invalid_credit_card_details_content_2')}</p>
        )
    }, [getLabel, getLocalUrlByLanguage]);
    
    useEffect(() => {
        if (status && status !== 'success') {
            setShowLoader(false);
            if (alertRef.current) {
                alertRef.current.show({
                    type: alertType.alert,
                    title: getLabel('billing_invalid_credit_card_details'),
                    content: contentToAlertCreditCard,
                    button_text: getLabel('billing_failed_sub_button_again'),
                    double_button_text: getLabel('billing_failed_sub_button_contact'),
                    onButtonClick: () => {
                        navigate(getLocalUrlByLanguage('/purchase'));
                    },
                    onDoubleButtonClick: () => {
                        navigate(getLocalUrlByLanguage('/contact_us'));
                    }
                });
            }
            return;
        }
        if (listProducts && listProducts.length > 0) {
            setShowLoader(false);
            (async () => {
                await syncUserData();
                try { sessionStorage.setItem('justPurchasedAt', Date.now().toString()); } catch (_) {}
                window.location.href = getLocalUrlByLanguage('/');
            })();
            return;
        }

        let timerId = null;
        const checkServerStatus = async () => {

            if (completed) {
                return;
            }
            try {
                const response = await userApi.GetUserData(getJwtToken());
                if (response && response.listProducts && response.listProducts.length > 0) {
                    setCompleted(true);
                    if (timerId) {
                        clearInterval(timerId);
                    }
                    setShowLoader(false);
                    await syncUserData();
                    try { sessionStorage.setItem('justPurchasedAt', Date.now().toString()); } catch (_) {}
                    window.location.href = getLocalUrlByLanguage('/');
                    return;
                }
            } catch (error) {
                console.error('Error checking server status:', error);
            }

            if (!completed) {
                setAttempts(prevAttempts => {
                    const newAttempts = prevAttempts + 1;
                    if (newAttempts > MAX_ATTEMPTS) {
                        if (timerId) {
                            clearInterval(timerId);
                        setShowLoader(false);
                        if (alertRef.current) {
                            alertRef.current.show({
                                type: alertType.alert,
                                title: getLabel('billing_failed_title'),
                                content: contentToAlert,
                                button_text: getLabel('billing_failed_sub_button_contact'),
                                onButtonClick: () => {
                                    navigate(getLocalUrlByLanguage('/contact_us'));
                                },
                                double_button_text: getLabel('billing_failed_sub_button_again'),
                                onDoubleButtonClick: () => {
                                    navigate(getLocalUrlByLanguage('/purchase'));
                                }
                            });
                        }
                    }
                }
                    return newAttempts;
                });
            }
        };
        checkServerStatus();
        timerId = setInterval(checkServerStatus, 10000);
        return () => {
            if (timerId) {
                clearInterval(timerId);
            }
        };
    }, [listProducts, getJwtToken, completed, navigate, getLocalUrlByLanguage, getLabel, syncUserData]);

    return (
        <div style={{ margin: 'auto' }}>
            {showLoader &&
                <div className={styles.wrap_loader}><Loader />
                    <span className={styles.billing_loading}>{getLabel("billing_loading")}</span>
                </div>}
            <Alert ref={alertRef}/>
        </div >
    );
};

export default Waiting;